import { VolumeOff, VolumeOn } from "assets/icons/Common";
import { Col, IconButtonInverted } from "components/primitives";
import { lerp } from "components/utils";
import { useMicrophoneLevels } from "hooks/microphoneLevels";
import Slider from "rc-slider";
import { useEffect, useState } from "react";
import "rc-slider/assets/index.css";
import styled from "styled-components";
import { WidgetFC } from "types/Widget";
import useSound from "use-sound";

export interface NoiseMeterState {
  muted: boolean;
}

const noiseThreshold = 5;

// Radial Bar constants
const radius = 100;
const thickness = 30;
const arcSize = 270;

export const NoiseMeter: WidgetFC<NoiseMeterState> = ({
  state,
  setState,
  settings,
  setSetting,
  pixelPosition,
}) => {
  const { mic, noise, sound, selectSound, permission } = settings;
  useMicrophoneLevels({
    onMicrophoneLevel,
    onMicrophoneGranted,
    onMicrophoneDenied,
    selectedDeviceId: mic,
  });
  const [noiseRatio, setNoiseRatio] = useState(0);
  const [noiseRatioCache, setNoiseRatioCache] = useState<number[]>([]);
  // The requirement is that sounds can't play more than once every 3 seconds
  // and they can only play once each time the needle goes over the limit.
  // We use the following two bools to capture those states
  const [isSoundOnCooldown, setIsSoundOnCooldown] = useState(false);
  const [canPlaySound, setCanPlaySound] = useState(true);
  const [hasPermission, setHasPermission] = useState(permission === "granted");
  const selectedSound = require("assets/sounds/" + (selectSound || "yoo-hoo.mp3"));
  const [play] = useSound(selectedSound);
  const loudThreshold = Number(noise);

  const mutableNoiseRatio = state.muted ? 0 : noiseRatio;
  // const hasPermission = permission === "granted";
  // console.log(permission);

  const size = (pixelPosition.width || 400) * 0.9;
  // localStorage.setItem("microphone-permission", "granted");
  //setHasPermission(true);

  useEffect(() => {
    let status: PermissionStatus;

    navigator.permissions
      .query({ name: "microphone" as PermissionName })
      .then((permissionStatus) => {
        status = permissionStatus;
        setSetting("permission", permissionStatus.state);
        localStorage.setItem("microphone-permission", permissionStatus.state);
        setHasPermission(permissionStatus.state === "granted");

        permissionStatus.onchange = () => {
          setSetting("permission", permissionStatus.state);
          localStorage.setItem("microphone-permission", permissionStatus.state);
          setHasPermission(permissionStatus.state === "granted");
        };
      });

    return () => {
      if (status) {
        status.onchange = null;
      }
    };
  }, []);

  useEffect(() => {
    if (noiseRatioCache.length > 5) {
      let newCache = [...noiseRatioCache];
      newCache.splice(0, 1);
      setNoiseRatioCache(newCache);
    }

    const averageNoiseLevel = noiseRatioCache.reduce((a, b) => a + b, 0) / noiseRatioCache.length;
    const newNoiseRatio = lerp(noiseRatio || 0, averageNoiseLevel || 0, 0.3);
    // console.log(noiseRatioCache);
    setNoiseRatio(newNoiseRatio);
    if (sound && !state.muted) {
      if (newNoiseRatio - 0.05 > loudThreshold) {
        if (!isSoundOnCooldown && canPlaySound) {
          play();
          setIsSoundOnCooldown(true);
          setCanPlaySound(false);
          setTimeout(() => {
            setIsSoundOnCooldown(false);
          }, 3000);
        }
      } else if (!isSoundOnCooldown && !canPlaySound) {
        setCanPlaySound(true);
      }
    }
  }, [noiseRatioCache]);

  function onMicrophoneLevel(level: number) {
    const clampedLevel = Math.min(level / noiseThreshold, 1);
    setNoiseRatioCache((prev) => [...prev, clampedLevel]);
  }

  function onMicrophoneGranted() {
    console.log("Granted");
  }

  function onMicrophoneDenied() {
    console.log("Denied");
  }

  const getSVGCircle = (progress: number, colour: string, shouldAnimate: boolean) => {
    const progressRatio = progress * 0.75 * 100;
    const circumference = 2 * Math.PI * (radius - thickness);
    const strokeLength = (circumference / 100) * progressRatio;
    return (
      <ProgressCircle
        stroke={colour}
        strokeWidth={thickness}
        strokeLinecap="butt"
        fill="none"
        cx={radius}
        cy={radius}
        r={radius - thickness}
        strokeDasharray={`${strokeLength},${circumference}`}
        shouldAnimate={shouldAnimate}
      />
    );
  };

  const toggleMute = () => {
    setState({
      muted: !state.muted,
    });
  };

  return (
    <OuterContainer>
      <InnerContainer>
        <GraphContainer>
          <ProgressRing viewBox={`0 0 ${radius * 2} ${radius * 2}`} width={size} height={size}>
            <g transform={`rotate(${arcSize * loudThreshold} 100 100)`}>
              {getSVGCircle(
                1 - loudThreshold,
                hasPermission ? "rgba(255, 144, 144, 0.5)" : "#FF9090",
                false,
              )}
            </g>
            {getSVGCircle(
              loudThreshold,
              hasPermission ? "rgba(127, 223, 219, 0.5)" : "#FF9090",
              false,
            )}
            {getSVGCircle(mutableNoiseRatio, "rgb(255, 144, 144)", true)}
            {getSVGCircle(Math.min(mutableNoiseRatio, loudThreshold), "rgb(127, 223, 219)", true)}
          </ProgressRing>
          <Needle angle={136 + arcSize * mutableNoiseRatio} />
        </GraphContainer>
        {hasPermission ? (
          <SliderContainer>
            <p>Max Noise</p>
            <Slider
              style={{ height: "100%" }}
              marks={{
                0: <></>,
                16.6: <></>,
                33.2: <></>,
                50: <></>,
                66.5: <></>,
                83: <></>,
                100: <></>,
              }}
              step={null}
              onChange={(value) => setSetting("noise", Number(value) / 100)}
              defaultValue={loudThreshold * 100}
              value={loudThreshold * 100}
              trackStyle={{
                backgroundColor: "var(--primary)",
                height: "2cqh",
                transform: "translateY(-50%)",
                top: "50%",
              }}
              railStyle={{
                backgroundColor: "#DDE1E8",
                height: "2cqh",
                transform: "translateY(-50%)",
                top: "50%",
              }}
              dotStyle={{
                top: "50%",
                borderRadius: "1cqw",
                height: "8cqw",
                width: "2cqw",
                backgroundColor: "#DDE1E8",
                transform: "translate(-50%, -50%)",
                border: "none",
              }}
              activeDotStyle={{
                backgroundColor: "var(--primary)",
              }}
              handleStyle={{
                backgroundColor: "var(--primary)",
                borderColor: "var(--primary)",
                width: "9cqw",
                height: "9cqw",
                opacity: "1",
                top: "50%",
                transform: "translate(-50%, -50%)",
                marginTop: "0px",
              }}
            />
          </SliderContainer>
        ) : (
          <NoPermissionContainer>
            <NoPermission>You need to provide microphone permission</NoPermission>
          </NoPermissionContainer>
        )}
        {state.muted ? (
          <MuteButton
            disabled={!hasPermission}
            withShadow={false}
            accentColor="var(--danger)"
            Icon={VolumeOff}
            onClick={toggleMute}
          />
        ) : (
          <MuteButton
            disabled={!hasPermission}
            withShadow={false}
            Icon={VolumeOn}
            onClick={toggleMute}
          />
        )}
      </InnerContainer>
    </OuterContainer>
  );
};

const OuterContainer = styled(Col)`
  height: 100%;
  justify-content: center;
  align-items: center;
`;

const InnerContainer = styled(Col)`
  height: 100%;
  width: 100%;
  justify-content: space-evenly;
  align-items: center;
`;

const GraphContainer = styled.div`
  position: relative;
`;

// The 135 in here defines the parent rotation of the circles.
const ProgressRing = styled.svg`
  transform: rotate(135deg);
  transform-origin: center;
`;

const ProgressCircle = styled.circle<{ shouldAnimate: boolean }>`
  transition: ${(props) => (props.shouldAnimate ? "0.15s linear" : "none")};
`;

interface NeedleProps {
  angle: number;
}

const Needle = styled.div.attrs<NeedleProps>(({ angle }) => ({
  style: {
    transform: `rotate(${angle + 1}deg)`,
  },
}))<NeedleProps>`
  background-color: #8f84de;
  width: 30cqw;
  height: 6px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform-origin: left;
  border-radius: 3px;
  transition: transform 0.15s linear;
`;

const MuteButton = styled(IconButtonInverted)`
  width: 18cqw;
  height: 18cqw;
`;

const SliderContainer = styled.div`
  height: 5cqh;
  width: 75%;
  position: relative;

  p {
    position: absolute;
    top: -120%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: var(--secondary);
    font-size: 8cqw;
    width: 100%;
    text-align: center;
  }

  .rc-slider-step {
    height: 100%;
    position: relative;
  }
`;

const NoPermissionContainer = styled.div`
  position: relative;
  width: 100%;
  height: 5cqh;
`;

const NoPermission = styled.p`
  font-size: 6cqw;
  text-align: center;
  color: var(--secondary);
  position: absolute;
  top: -8cqh;
`;
